import { defineComponent as _defineComponent } from 'vue'
import { queries } from "@/apollo";
import { User, UserStakes } from "@/CodegenTypes";

import xlsx from "xlsx";

import { sumBy, first, sortBy } from "lodash";
import { formatDollar } from "@/helpers/formatDollar";
import TTNumberStakeModal from "@/components/modules/bet-details/TTNumberStakeModal.vue";
import { customMoment } from "@/main";
import Clusterize from "clusterize.js";
import { useTranslation } from "@/store/translation";
import InputText from "primevue/inputtext/InputText";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRoute } from "vue-router/composables";

type SortMode =
  | "number-asc"
  | "number-desc"
  | "inBig-asc"
  | "inBig-desc"
  | "inSmall-asc"
  | "inSmall-desc"
  | "inAmount-asc"
  | "inAmount-desc"
  | "intakeBig-asc"
  | "intakeBig-desc"
  | "intakeSmall-asc"
  | "intakeSmall-desc"
  | "intakeAmount-asc"
  | "intakeAmount-desc"
  | "outBig-asc"
  | "outBig-desc"
  | "outSmall-asc"
  | "outSmall-desc"
  | "outAmount-asc"
  | "outAmount-desc"
  | "maxPayout-asc"
  | "maxPayout-desc";

type RouteQueryValue = string | (string | null)[] | null | undefined;


export default /*#__PURE__*/_defineComponent({
  __name: 'TTUserStake',
  setup(__props) {

const route = useRoute();
const { $t } = useTranslation();
const t = (text: string) => $t(text);

const numberFilter = ref("");
const selectedNumber = ref<null | string>(null);
const showNumberStakeModal = ref(false);
const sortMode = ref<SortMode>("number-asc");
const clusterize = ref<Clusterize | null>(null);
const startIndex = ref(0);
const PER_PAGE = 100;
let clusterizeMounted = false;

const selectedUserId = computed(() => getRouteQueryValue(route.query.userId));
const selectedReleaseDate = computed(() =>
  getRouteQueryValue(route.query.startReleaseDate)
);
const userVariables = computed(() => {
  return {
    userId: selectedUserId.value,
  };
});
const ttUserStakesVariables = computed(() => {
  return {
    releaseDate: customMoment(selectedReleaseDate.value).startOf("day").toDate(),
    appMode: "DD",
    behalfOf: selectedUserId.value,
  };
});

const { result: userResult, loading: userLoading } = useQuery<{ user: User }>(
  queries.user,
  userVariables,
  {
    enabled: computed(() => !!selectedUserId.value),
    fetchPolicy: "cache-and-network",
  }
);
const { result: ttUserStakesResult, loading: ttUserStakesLoading } = useQuery<{
  ttUserStakes: UserStakes;
}>(queries.ttUserStakes, ttUserStakesVariables, {
  enabled: computed(() => !!selectedReleaseDate.value && !!selectedUserId.value),
  fetchPolicy: "cache-and-network",
});

const user = computed(() => userResult.value?.user ?? null);
const ttUserStakes = computed(
  () => ttUserStakesResult.value?.ttUserStakes ?? null
);
const apolloLoading = computed(
  () => userLoading.value || ttUserStakesLoading.value
);
const stakeRows = computed(() => {
  if (!ttUserStakes.value) {
    return [];
  }
  const downlineEatStake = ttUserStakes.value.downlineEatStake;
  // const companyFinalStake = ttUserStakes.value.companyFinalStake;
  const rows = ttUserStakes.value.masterStake
    .filter((row) => row.number.includes(numberFilter.value))
    .map((row, $index) => {
      const intakeRow = downlineEatStake[$index];
      // const outRow = companyFinalStake[$index];
      const amountIn = row.unit;
      const amountIntake = intakeRow.unit;

      const amountOut = amountIn - amountIntake;
      // const maxPayout = Math.max(bigIn * 4000, smallIn * 3000);
      const maxPayout = 0;
      return {
        number: row.number,
        amountIn,
        amountIntake,
        amountOut,
        maxPayout,
      };
    });
  return sortBy(rows, (row) => {
    switch (sortMode.value) {
      case "number-asc":
        return row.number;
      case "number-desc":
        return -row.number;

      case "inAmount-asc":
        return row.amountIn;
      case "inAmount-desc":
        return -row.amountIn;

      case "intakeAmount-asc":
        return row.amountIntake;
      case "intakeAmount-desc":
        return -row.amountIntake;

      case "outAmount-asc":
        return row.amountOut;
      case "outAmount-desc":
        return -row.amountOut;
      case "maxPayout-asc":
        return row.maxPayout;
      case "maxPayout-desc":
        return -row.maxPayout;
    }
  });
});
const stakeRowsPaginated = computed(() => {
  const endIndex = startIndex.value + PER_PAGE;
  return stakeRows.value.slice(startIndex.value, endIndex);
});
const stakeSummary = computed(() => {
  return {
    amountIn: sumBy(stakeRows.value, (r) => r.amountIn),
    amountIntake: sumBy(stakeRows.value, (r) => r.amountIntake),
    amountOut: sumBy(stakeRows.value, (r) => r.amountOut),
  };
});
const renderedRows = computed(() => {
  const sortedRows = sortBy(stakeRows.value, (r) => r.number.length);
  return sortedRows.map((row, $index) => {
    return `
      <tr class="text-center">
        <td>${$index + 1}</td>
        <td>
              <a
                id="${row.number}"
                class="font-bold text-primary-600 cursor-pointer number-row"
                >${row.number}</a
              >
            </td>
            <td colspan="3">${formatDollar(row.amountIn)}</td>
            <td colspan="3">${formatDollar(row.amountIntake)}</td>
            <td colspan="3">${formatDollar(row.amountOut)}</td>
            <td>${formatDollar(row.maxPayout)}</td>
      </tr>
      `;
  });
});

watch(stakeRows, () => {
  if (clusterizeMounted) {
    doClusterize();
  }
});

onMounted(() => {
  clusterizeMounted = true;
  doClusterize();
});

onBeforeUnmount(() => {
  clusterizeMounted = false;
  clusterize.value?.destroy(true);
  clusterize.value = null;
});

function getRouteQueryValue(query: RouteQueryValue) {
  return Array.isArray(query) ? first(query) : query;
}

function doClusterize() {
  if (clusterize.value) {
    clusterize.value.update(renderedRows.value);
    return;
  }

  clusterize.value = new Clusterize({
    rows: renderedRows.value,
    scrollId: "scrollArea",
    contentId: "contentArea",
    show_no_data_row: false,
    rows_in_block: 20,
    callbacks: {
      clusterChanged: bindNumberRows,
    },
  });
}

function bindNumberRows() {
  const numberRows = document.getElementsByClassName("number-row");
  for (const row of numberRows) {
    const numberRow = row as HTMLElement;
    numberRow.addEventListener("click", () => {
      handleSelectNumber(numberRow.id);
    });
  }
}

function formatDate(dateStr: string) {
  return customMoment(dateStr).format("DD MMM YYYY");
}

function handleSelectNumber(number: string) {
  selectedNumber.value = number;
  showNumberStakeModal.value = true;
}

function exportExcel() {
  if (!user.value || !stakeSummary.value) {
    return;
  }
  // const table = this.$refs.table;
  // const wb = xlsx.utils.table_to_book(table as HTMLElement, { raw: true });

  const wb = xlsx.utils.book_new();

  const ws_data = [] as string[][];
  const metaRow1 = [`Generated at ${customMoment()}`];
  ws_data.push(metaRow1);
  const metaRow2 = [
    `Account ${user.value.userId.toUpperCase()} ${formatDate(
      selectedReleaseDate.value || ""
    )} Stake`,
  ];
  ws_data.push(metaRow2);
  const headerRow1 = ["", "In", "Intake", "Out", "First Prize"];
  ws_data.push(headerRow1);

  const headerRow2 = ["Number", "Amount", "Amount", "Amount", "Max Payout"];
  ws_data.push(headerRow2);

  for (const row of stakeRows.value) {
    const rowData = [
      row.number,
      formatDollar(row.amountIn),
      formatDollar(row.amountIntake),
      formatDollar(row.amountOut),
      formatDollar(row.maxPayout),
    ];
    ws_data.push(rowData);
  }
  const footerData = [
    `Total (${stakeRows.value.length})`,
    formatDollar(stakeSummary.value.amountIn),
    formatDollar(stakeSummary.value.amountIntake),
    formatDollar(stakeSummary.value.amountOut),
    "",
  ];
  ws_data.push(footerData);

  const ws = xlsx.utils.aoa_to_sheet(ws_data);
  // ws["!merges"] = [
  // { s: { c: 0, r: 0 }, e: { c: 10, r: 0 } },
  // { s: { c: 0, r: 1 }, e: { c: 10, r: 1 } },
  // { s: { c: 1, r: 2 }, e: { c: 3, r: 2 } },
  // { s: { c: 4, r: 2 }, e: { c: 6, r: 2 } },
  // { s: { c: 7, r: 2 }, e: { c: 9, r: 2 } }
  // ];
  xlsx.utils.book_append_sheet(wb, ws, "Sheet 1");

  xlsx.writeFile(wb, `${user.value?.userId.toUpperCase()}-stake.xlsx`, {
    bookType: "xlsx",
  });
  return;
}

return { __sfc: true,route, $t, t, numberFilter, selectedNumber, showNumberStakeModal, sortMode, clusterize, startIndex, PER_PAGE, clusterizeMounted, selectedUserId, selectedReleaseDate, userVariables, ttUserStakesVariables, userResult, userLoading, ttUserStakesResult, ttUserStakesLoading, user, ttUserStakes, apolloLoading, stakeRows, stakeRowsPaginated, stakeSummary, renderedRows, getRouteQueryValue, doClusterize, bindNumberRows, formatDate, handleSelectNumber, exportExcel, formatDollar, TTNumberStakeModal, InputText }
}

})